import { type PowerSyncDrizzleDB, user } from "@tendly/powersync";
import { patchRowVerified, type VerifiedWriteOptions } from "../powersync/verified-write";

/**
 * ── Why EVERY mutation below uses `.where(eq(user.id, userId))` ─
 *
 * Four of them (`locale`, `global_theme_id`, `is_blur`,
 * `follow_device_theme`) had this shape:
 *
 *     const query = db.update(user).set({ … });
 *     await (userId === VISITOR_ID ? query : query.where(eq(user.id, userId)));
 *
 * that is, in visitor mode the UPDATE went out **without `WHERE`** → rewrote ALL
 * rows in the local `user` table. The branch existed for symmetry with
 * `getUserThemeSettingsQuery`, which for the visitor reads with `.limit(1)` without
 * a filter; but a read `limit(1)` and an unfiltered UPDATE are not the same
 * bet: the first picks the wrong row, the latter wipes them all.
 *
 * The premise on which it hung ("the visitor database only has one
 * `user` row") is false in a specific window: during the visitor→real upgrade
 * the visitor database CONNECTS with the real account's connector and subscribes
 * itself to all streams — including `organizations`, which synchronizes
 * one `user` row for EACH co-member of that account's organizations.
 * There the table has 1 + 1 + N rows, `user` is NOT `localOnly`, and each
 * touched row queues its own op in `ps_crud`.
 *
 * The correct filter is NOT guesswork: the visitor row is seeded by
 * `ThemeProvider` (`apps/web/src/providers/theme-provider.tsx`) with
 * `db.insert(user).values({ id: uid, … })`, and for a visitor tab `uid`
 * is exactly `VISITOR_ID`. Thus `eq(user.id, userId)` points to the SAME
 * row the unfiltered branch intended to touch when the table had one row —
 * identical behavior in the normal case, bounded in the rest. It is also
 * the form `updateUserFontFamilyMutation` and
 * `updateUserImageMutation` already used, which never had a visitor branch.
 *
 * Covered by `packages/shared/src/users/__tests__/mutations-where.test.ts`.
 *
 * ── Why NONE of these mutations is left outside the anti-window guard
 * — updated 2026-08-13 after the ThemeProvider seed guard ────────────────
 *
 * `patchRowVerified` (`packages/shared/src/powersync/verified-write.ts`) writes,
 * rereads and retries because an `UPDATE … WHERE id = ?` against PowerSync's view
 * can match zero rows and return "success" while the row travels to the
 * server. `user` is NOT excluded from this guard even though in other domains
 * the session/user table is usually the obvious case to exclude, and that remains
 * true even though `ThemeProvider` is no longer part of the problem (see
 * below):
 *
 *   - `drizzle()`/`db` (the PowerSync accessor) becomes non-null as soon as
 *     the local SQLite opens, not when the initial sync finishes.
 *   - `userId()` (`apps/web/src/lib/auth.ts`) becomes active as soon as
 *     `signUpActor`/`signInActor` receive the server session
 *     (`packages/shared/src/auth/controllers.ts`) — a REST call
 *     independent of the PowerSync stream.
 *
 * These two points alone are enough: between `db` and `userId()` becoming ready
 * and the initial checkpoint bringing the `user` row the server created at
 * signup, there is a window in which a normal `UPDATE` (change language,
 * theme...) hits a row the local view does not yet have — it matches ZERO
 * rows and silently returns "success". That window does not depend on anyone
 * seeding a local row: it is the syncing view, nothing more.
 *
 * (Historical context, now CLOSED) Before the guard above in
 * `theme-provider.tsx`, there was a third factor that worsened this same
 * window for real accounts: `ThemeProvider`'s `createEffect`
 * (`apps/web/src/providers/theme-provider.tsx`) locally inserted a
 * 3-column "ghost" `user` row for ANY `uid`, without distinguishing
 * visitor from real account, competing with the real row the server had
 * already created (`packages/core-rust/src/db/user.rs:101,350`) and that was on
 * its way through sync. The `uid !== VISITOR_ID` guard closed that concrete
 * root cause: the effect now only seeds for the visitor. **This does NOT make
 * the `patchRowVerified` protection for `user` unnecessary** — the two
 * points above (`db` and `userId()` ready before the checkpoint) still open
 * the same window for any early write, with or without a ghost row in between.
 * `patchRowVerified` here is generic defense in depth for the checkpoint, not a
 * patch left over from the already-closed bug — if it is ever removed, that
 * should be because the window above is proven no longer to exist, not because
 * `ThemeProvider` stopped seeding ghosts.
 *
 * Measurement: `packages/shared/src/powersync/__tests__/verified-write.test.ts`
 * (`user` cases) and `packages/shared/src/users/__tests__/theme-provider-seed-window.test.ts`.
 */

/**
 * Shared Drizzle mutation for updating User locale.
 */
export async function updateUserLocaleMutation(
  db: PowerSyncDrizzleDB,
  userId: string,
  locale: string,
  options: VerifiedWriteOptions = {},
): Promise<void> {
  await patchRowVerified(db, user, user.id, userId, { locale }, options);
}

/**
 * Shared Drizzle mutation for updating User font family preference.
 */
export async function updateUserFontFamilyMutation(
  db: PowerSyncDrizzleDB,
  userId: string,
  fontFamilyId: string | null,
  options: VerifiedWriteOptions = {},
): Promise<void> {
  await patchRowVerified(db, user, user.id, userId, { font_family: fontFamilyId }, options);
}

/**
 * Writes the avatar KEY to the local row.
 *
 * It is written local-first (like language, font and theme) and not via REST: this way
 * the new photo appears instantly and survives a cold reload without
 * a connection. The BINARY does go via REST (`POST /api/avatar`) because it does not fit in the
 * sync queue; what travels here is only the key returned by that upload.
 *
 * `null` = "remove the photo" (initials return).
 *
 * The visitor has no own synchronizable row and cannot have an avatar (rule
 * zero), so there is no `VISITOR_ID` branch here as in `locale`/`theme`.
 */
export async function updateUserImageMutation(
  db: PowerSyncDrizzleDB,
  userId: string,
  imageKey: string | null,
  options: VerifiedWriteOptions = {},
): Promise<void> {
  await patchRowVerified(db, user, user.id, userId, { image: imageKey }, options);
}

/**
 * Shared Drizzle mutation for updating global theme.
 */
export async function updateGlobalThemeMutation(
  db: PowerSyncDrizzleDB,
  userId: string,
  themeId: string | null,
  options: VerifiedWriteOptions = {},
): Promise<void> {
  await patchRowVerified(db, user, user.id, userId, { global_theme_id: themeId }, options);
}

/**
 * Shared Drizzle mutation for updating blur effect preference.
 */
export async function updateBlurMutation(
  db: PowerSyncDrizzleDB,
  userId: string,
  isBlur: boolean,
  options: VerifiedWriteOptions = {},
): Promise<void> {
  await patchRowVerified(db, user, user.id, userId, { is_blur: isBlur ? 1 : 0 }, options);
}

/**
 * Shared Drizzle mutation for updating follow device appearance preference.
 */
export async function updateFollowDeviceThemeMutation(
  db: PowerSyncDrizzleDB,
  userId: string,
  followDevice: boolean,
  options: VerifiedWriteOptions = {},
): Promise<void> {
  await patchRowVerified(
    db,
    user,
    user.id,
    userId,
    { follow_device_theme: followDevice ? 1 : 0 },
    options,
  );
}
